import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Eye, EyeOff, Lock, ArrowRight, Users } from 'lucide-react'
import { loginUser, clearAuthError } from '../Login/services/authSlice'

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLoading, isError, message, isAuth } = useSelector((state) => state.auth)

  useEffect(() => {
    if (isAuth) navigate('/dashboard')
  }, [isAuth, navigate])

  useEffect(() => {
    return () => { dispatch(clearAuthError()) }
  }, [dispatch])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const result = await dispatch(loginUser({ username, password }))
    if (loginUser.fulfilled.match(result)) {
      navigate('/dashboard')
    }
  }

  return (
    <>
      <div className="login-container">
        <div className="login-background"></div>
        <div className="login-content">
          <div className="login-form-section">
            <div className="login-card">
              <div className="login-header">
                <h1>Welcome Back</h1>
                <p>Sign in to your admin dashboard</p>
              </div>

              <form className="login-form" onSubmit={handleSubmit}>
                {isError && <div className="login-error">{message}</div>}

                <div className="form-group">
                  <label className="form-label">Username</label>
                  <div className="input-wrapper">
                    <Users size={18} className="input-icon" />
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter username"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Password</label>
                  <div className="input-wrapper">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showPwd ? 'text' : 'password'}
                      className="form-input"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="input-toggle"
                      onClick={() => setShowPwd((s) => !s)}
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="login-submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="loading-spinner" />
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <div className="login-footer">
                <p>Demo credentials are pre-filled for easy access</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0D1220 0%, #1a1f35 50%, #0D1220 100%);
          position: relative;
          overflow: hidden;
        }
        .login-background {
          position: absolute;
          inset: 0;
          overflow: hidden;
          background-attachment: fixed;
          background-image: url("/images/white_background.jpg");
          background-size: cover;
          background-position: center;
        }
        .login-content {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 4rem;
          width: 100%;
          padding: 2rem;
          position: relative;
          z-index: 1;
        }
        .login-form-section {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .login-card {
          background: linear-gradient(135deg, rgba(37, 47, 78, 0.85), rgba(41, 52, 76, 0.9));
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 2.5rem;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
          position: relative;
        }
        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5;
          font-size: 0.875rem;
          padding: 0.6rem 0.9rem;
          border-radius: 10px;
          margin-bottom: -0.5rem;
        }
        .login-header { text-align: center; margin-bottom: 2rem; }
        .login-header h1 { font-size: 2rem; font-weight: 700; color: white; letter-spacing: -0.02em; }
        .login-header p { font-size: 1rem; color: rgba(255, 255, 255, 0.8); }
        .login-form { display: flex; flex-direction: column; gap: 1.5rem; }
        .form-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .form-label { font-size: 0.875rem; font-weight: 500; color: rgba(255, 255, 255, 0.9); }
        .input-wrapper { position: relative; display: flex; align-items: center; }
        .input-icon {
          position: absolute; left: 1rem; top: 50%;
          transform: translateY(-50%); pointer-events: none;
          color: #0d1530; z-index: 2;
        }
        .form-input {
          width: 100% !important;
          background: #fff !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          border-radius: 12px !important;
          color: #0D1530 !important;
          font-size: 1rem !important;
          padding: 0.875rem 1rem 0.875rem 3rem !important;
          outline: none !important;
          transition: all 0.2s ease !important;
          box-sizing: border-box;
          line-height: 1;
        }
        .form-input:focus {
          border-color: var(--accent);
          background: rgba(255, 255, 255, 0.15);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .form-input::placeholder { color: rgba(255, 255, 255, 0.5); }
        .input-toggle {
          position: absolute; right: 1rem; background: none; border: none;
          color: rgba(255, 255, 255, 0.6); cursor: pointer; padding: 0.25rem;
          border-radius: 4px; transition: color 0.2s ease;
        }
        .input-toggle:hover { color: white; }
        .login-submit {
          width: 100%;
          background: linear-gradient(135deg, var(--accent), var(--accent-hover));
          border: none; border-radius: 12px; color: white;
          font-size: 1rem; font-weight: 600; padding: 0.875rem 1.5rem;
          display: flex; align-items: center; justify-content: center; gap: 0.5rem;
          cursor: pointer; transition: all 0.2s ease;
          box-shadow: 0 4px 16px rgba(79, 70, 229, 0.3);
        }
        .login-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(79, 70, 229, 0.4);
        }
        .login-submit:disabled { opacity: 0.7; cursor: not-allowed; }
        .loading-spinner {
          width: 20px; height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white; border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-footer {
          text-align: center; margin-top: 2rem; padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .login-footer p { font-size: 0.875rem; color: rgba(255, 255, 255, 0.6); }
        @media (max-width: 640px) {
          .login-container { padding: 1rem; }
          .login-card { padding: 2rem; }
          .login-header h1 { font-size: 1.5rem; }
        }
      `}</style>
    </>
  )
}